<template>
    <main class="flex-1 overflow-auto">
      <div class="p-6 lg:p-8">
        <!-- Page Header -->
        <div class="mb-8">
          <h1 class="text-2xl lg:text-3xl font-bold text-foreground">Dashboard</h1>
          <p class="text-muted-foreground mt-1">Vue d'ensemble de votre boutique</p>
        </div>
        
        <!-- Stats Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
          <div class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-shadow">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-muted-foreground text-sm">Produits</p>
                  <p class="text-2xl font-bold mt-1">{{ stats.totalProducts }}</p>
                </div>
                <div class="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <svg class="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-shadow">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-muted-foreground text-sm">Commandes</p>
                  <p class="text-2xl font-bold mt-1">{{ stats.totalOrders }}</p>
                </div>
                <div class="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center">
                  <svg class="h-6 w-6 text-secondary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-shadow">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-muted-foreground text-sm">Utilisateurs</p>
                  <p class="text-2xl font-bold mt-1">{{ stats.totalUsers }}</p>
                </div>
                <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-shadow">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-muted-foreground text-sm">Fournisseurs</p>
                  <p class="text-2xl font-bold mt-1">{{ stats.totalSuppliers }}</p>
                </div>
                <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM12 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.5 9a.5.5 0 01.5-.5h4a.5.5 0 01.5.5v4a.5.5 0 01-.5.5h-4a.5.5 0 01-.5-.5V9z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-shadow sm:col-span-2 lg:col-span-1">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-muted-foreground text-sm">Chiffre d'affaires</p>
                  <p class="text-xl font-bold mt-1">{{ formatPrice(stats.revenue) }}</p>
                  <div class="flex items-center mt-1 text-green-600 text-sm" v-if="stats.growth > 0">
                    <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                    </svg>
                    <span>+{{ stats.growth }}%</span>
                  </div>
                </div>
                <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Recent Orders -->
          <div class="bg-card border border-border rounded-lg shadow-sm lg:col-span-2">
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h2 class="text-lg font-semibold">Commandes récentes</h2>
                <div v-if="loading" class="text-sm text-muted-foreground">
                  Chargement...
                </div>
              </div>
              <div class="overflow-x-auto" v-if="recentOrders.length > 0">
                <table class="w-full">
                  <thead>
                    <tr class="border-b">
                      <th class="text-left py-3 px-2 text-sm font-medium text-muted-foreground">N° Commande</th>
                      <th class="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Client</th>
                      <th class="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Date</th>
                      <th class="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Statut</th>
                      <th class="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="order in recentOrders" :key="order.id" class="border-b last:border-0 hover:bg-muted/50 transition-colors">
                      <td class="py-3 px-2 text-sm font-medium">{{ order.order_number || `CMD-${order.id.toString().padStart(3, '0')}` }}</td>
                      <td class="py-3 px-2 text-sm">
                        {{ order.customer_name || (order.user ? order.user.name : 'N/A') }}
                      </td>
                      <td class="py-3 px-2 text-sm text-muted-foreground">
                        {{ formatDate(order.created_at) }}
                      </td>
                      <td class="py-3 px-2">
                        <span :class="`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`">
                          {{ getStatusLabel(order.status) }}
                        </span>
                      </td>
                      <td class="py-3 px-2 text-sm font-medium text-right">
                        {{ formatPrice(order.total) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-8 text-muted-foreground">
                Aucune commande récente
              </div>
            </div>
          </div>

          <!-- Top Products -->
          <div class="bg-card border border-border rounded-lg shadow-sm">
            <div class="p-6">
              <h2 class="text-lg font-semibold mb-4">Produits populaires</h2>
              <div class="space-y-4" v-if="topProducts.length > 0">
                <div v-for="(product, index) in topProducts" :key="product.id" class="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div class="flex items-center space-x-3">
                    <span class="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                      {{ index + 1 }}
                    </span>
                    <div>
                      <p class="font-medium text-sm truncate max-w-[120px]">{{ product.name }}</p>
                      <p class="text-xs text-muted-foreground">{{ product.sales || 0 }} ventes</p>
                    </div>
                  </div>
                  <p class="text-sm font-semibold">{{ formatPrice(product.revenue) }}</p>
                </div>
              </div>
              <div v-else class="text-center py-8 text-muted-foreground">
                Aucun produit populaire
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'AdminDashboard',
  data() {
    return {
      loading: false,
      statsData: {
        totalProducts: 0,
        totalOrders: 0,
        totalUsers: 0,
        totalSuppliers: 0,
        revenue: 0,
        growth: 0
      }
    }
  },
  computed: {
    ...mapState([
      'admin',
      'dashproducts',
      'dashorders',
      'dashusers',
      'dashsuppliers',
      'adminSuppliers'
    ]),
    ...mapGetters([
      'adminStats',
      'adminSuppliersStats'
    ]),
    stats() {
      // Utiliser les données du store pour les statistiques
      const totalOrders = this.admin.orders.length || this.dashorders.length;
      const totalRevenue = this.calculateTotalRevenue();
      const previousMonthRevenue = totalRevenue * 0.9; // Simulation pour la croissance
      const growth = previousMonthRevenue > 0 
        ? ((totalRevenue - previousMonthRevenue) / previousMonthRevenue * 100).toFixed(1)
        : 0;

      return {
        totalProducts: this.admin.products.length || this.dashproducts.length || 0,
        totalOrders: totalOrders,
        totalUsers: this.admin.users.length || this.dashusers.length || 0,
        totalSuppliers: this.adminSuppliers.length || this.dashsuppliers.length || 0,
        revenue: totalRevenue,
        growth: parseFloat(growth)
      };
    },
    recentOrders() {
      // Récupérer les 4 dernières commandes
      const orders = this.admin.orders.length > 0 
        ? this.admin.orders 
        : this.dashorders;
      
      return orders
        .slice(0, 4)
        .map(order => ({
          ...order,
          customer: order.customer_name || (order.user ? order.user.name : 'Client')
        }));
    },
    topProducts() {
      // Calculer les produits les plus populaires basés sur les ventes
      // Pour l'instant, retourner les 3 premiers produits
      const products = this.admin.products.length > 0 
        ? this.admin.products.slice(0, 3)
        : this.dashproducts.slice(0, 3);
      
      return products.map(product => ({
        ...product,
        name: product.name || 'Produit sans nom',
        sales: Math.floor(Math.random() * 100) + 10, // À remplacer par les données réelles
        revenue: (product.price || 0) * (Math.floor(Math.random() * 100) + 10)
      }));
    }
  },
  methods: {
    ...mapActions([
      'fetchAdminData',
      'fetchDashboardData',
      'fetchAdminSuppliers'
    ]),
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price) + ' FCFA';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    getStatusColor(status) {
      switch (status) {
        case 4: // Livré
          return 'bg-green-100 text-green-800';
        case 3: // Expédié
          return 'bg-blue-100 text-blue-800';
        case 2: // Accepté
          return 'bg-purple-100 text-purple-800';
        case 1: // En cours
          return 'bg-yellow-100 text-yellow-800';
        case 0: // Rejeté
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },
    getStatusLabel(status) {
      const labels = {
        0: 'Rejeté',
        1: 'En cours',
        2: 'Accepté',
        3: 'Expédié',
        4: 'Livré'
      };
      return labels[status] || 'Inconnu';
    },
    calculateTotalRevenue() {
      const orders = this.admin.orders.length > 0 
        ? this.admin.orders 
        : this.dashorders;
      
      return orders.reduce((total, order) => {
        return total + (parseFloat(order.total) || 0);
      }, 0);
    },
    async loadData() {
      this.loading = true;
      try {
        // Charger les données du dashboard et des fournisseurs
        await Promise.all([
          this.fetchAdminData(),
          this.fetchDashboardData(),
          this.fetchAdminSuppliers()
        ]);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    this.loadData();
  }
}
</script>